import { useState, FormEvent } from 'react';
import { companyInfo } from '../../../mocks/homeData';

const FORM_URL = 'https://readdy.ai/api/form/d73g8hhttkj2t7sc9pdg';

const ContactSection = () => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);

    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    setStatus('loading');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-14">
          <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-3">Contact Us</p>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight">
            聯絡方式
          </h2>
          <p className="mt-4 text-stone-500 text-sm leading-relaxed max-w-md">
            有任何室內裝修需求，歡迎隨時與我們聯繫，我們將提供專業建議與免費估價服務。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Info Cards */}
            <div className="bg-white rounded-2xl p-6 flex flex-col gap-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
                  <i className="ri-phone-line text-amber-700 text-lg" />
                </div>
                <div>
                  <p className="text-stone-500 text-xs mb-1">市內電話</p>
                  <a href={`tel:${companyInfo.phone.replace(/-/g, '')}`} className="text-stone-800 font-bold text-lg hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
                  <i className="ri-smartphone-line text-amber-700 text-lg" />
                </div>
                <div>
                  <p className="text-stone-500 text-xs mb-1">行動電話</p>
                  <a href={`tel:${companyInfo.mobile.replace(/-/g, '')}`} className="text-stone-800 font-bold text-lg hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap">
                    {companyInfo.mobile}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
                  <i className="ri-map-pin-line text-amber-700 text-lg" />
                </div>
                <div>
                  <p className="text-stone-500 text-xs mb-1">公司地址</p>
                  <p className="text-stone-800 font-semibold text-sm leading-relaxed">
                    {companyInfo.address}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
                  <i className="ri-time-line text-amber-700 text-lg" />
                </div>
                <div>
                  <p className="text-stone-500 text-xs mb-1">服務時間</p>
                  <p className="text-stone-800 font-semibold text-sm">
                    週一至週六｜08:00 – 18:00
                  </p>
                  <p className="text-stone-400 text-xs mt-0.5">例假日請來電確認</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden bg-white" style={{ height: '200px' }}>
              <iframe
                title="祐灃室內裝修位置"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3641.6!2d120.7!3d24.15!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s%E5%8F%B0%E4%B8%AD%E5%B8%82%E5%A4%AA%E5%B9%B3%E5%8D%80%E6%A8%B9%E5%AD%9D%E8%B7%AF274%E5%B7%A99%E5%BC%8121%E8%99%9F1%E6%A8%93!5e0!3m2!1szh-TW!2stw!4v1711500000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-7 md:p-8">
            <h3 className="text-stone-800 font-bold text-xl mb-2">免費諮詢 / 估價申請</h3>
            <p className="text-stone-400 text-sm mb-7">填寫表單後我們將於一個工作日內與您聯繫。</p>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-100">
                  <i className="ri-checkbox-circle-line text-amber-700 text-3xl" />
                </div>
                <p className="text-stone-700 font-semibold text-lg">感謝您的來信！</p>
                <p className="text-stone-400 text-sm text-center">我們已收到您的訊息，將盡快與您聯繫。</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 px-5 py-2 rounded-full border border-stone-200 text-stone-600 text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再次填寫
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-stone-600 text-xs font-medium">您的姓名 *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="王小明"
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors bg-stone-50"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-stone-600 text-xs font-medium">聯絡電話 *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="0912-345-678"
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors bg-stone-50"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-stone-600 text-xs font-medium">電子郵件</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors bg-stone-50"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-stone-600 text-xs font-medium">需要的服務 *</label>
                  <select
                    name="service"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-700 focus:outline-none focus:border-amber-500 transition-colors bg-stone-50 cursor-pointer"
                  >
                    <option value="" disabled>請選擇服務項目</option>
                    <option value="天花板工程">天花板工程</option>
                    <option value="地板工程">地板工程</option>
                    <option value="牆壁工程">牆壁工程</option>
                    <option value="隔間工程">隔間工程</option>
                    <option value="櫥櫃工程">櫥櫃工程</option>
                    <option value="全室裝修">全室裝修</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between items-center">
                    <label className="text-stone-600 text-xs font-medium">需求說明</label>
                    <span className={`text-xs ${charCount > 450 ? 'text-red-500' : 'text-stone-300'}`}>
                      {charCount} / 500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="請簡述您的裝修需求、地址或其他相關資訊..."
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors bg-stone-50 resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-xs">送出失敗，請稍後再試或直接來電聯繫。</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading' || charCount > 500}
                  className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-amber-700 text-white font-semibold text-sm hover:bg-amber-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      送出中...
                    </>
                  ) : (
                    <>
                      送出諮詢
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-send-plane-line text-sm" />
                      </div>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
